import { createRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@arabquiz/ui'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  Eye,
  Flag,
  Hash,
  Loader2,
  LogOut,
  MessageSquare,
  Play,
  Sparkles,
  Trophy,
  User,
  Users,
} from 'lucide-react'
import type { LeaderboardEntry, QuestionStartedPayload } from '@arabquiz/api-client'
import { Route as appRoute } from '../../../_app'
import { GameStateProvider, useGameState } from '../../../../game/GameStateProvider'
import { PlayerGameView } from '../../../../game/PlayerGameView'
import { readStoredSoloSession } from '../../../../game/playerTokenStorage'

/**
 * Host control panel — the screen a presenter projects on the big screen
 * while running a live game. Wraps its content in `GameStateProvider`
 * (which owns the SignalR connection) so the lifecycle of the websocket
 * is bound to time-on-this-route: navigating away tears the connection
 * down automatically via the provider's `useEffect` cleanup.
 *
 * The component is a phase-driven switchboard: each entry of
 * `GameStateData.phase` ('lobby' / 'question' / 'results' / 'leaderboard'
 * / 'final') routes to a dedicated sub-view. All sub-views render inside
 * the same dark-gradient shell so the room sees a consistent backdrop as
 * the host moves the game forward — only the foreground content swaps.
 *
 * Visual direction: projector-scale, dramatic, editorial restraint. Tajawal
 * display headings, oversized monospace numerals (PIN, timer, scores),
 * generous whitespace, and a single accent action per screen so the host
 * never has to hunt for the next click. All spacing and inset utilities
 * are logical (`ps-*`, `pe-*`, `start-*`, `end-*`) because the page is
 * Arabic-first and must mirror cleanly under `dir="rtl"`.
 *
 * On phase transition into `results` we drop into a local sub-view state
 * (`reveal` → `leaderboard`) controlled purely on the client. The server
 * stays in `ShowingResults`; the "show leaderboard" button is a UI
 * pacing affordance for the host, not a state mutation.
 */
export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/games/host/$gameId',
  component: HostGameRoute,
})

function HostGameRoute() {
  // The provider mounts the SignalR connection eagerly, so the inner
  // panel can call `invoke('JoinAsHost', ...)` inside its own effect.
  return (
    <GameStateProvider>
      <HostControlPanel />
    </GameStateProvider>
  )
}

type ResultsSubView = 'reveal' | 'leaderboard'

function HostControlPanel() {
  const { t } = useTranslation('game')
  const { gameId } = Route.useParams()
  const { state, invoke, dispatch } = useGameState()

  // Solo mode: the host plays their own game on this single device. Entered
  // from the lobby's "play solo" card, or restored on mount when a stored solo
  // session points at this game (a refresh mid-solo).
  const [soloMode, setSoloMode] = useState(false)

  // Bind to the game exactly once per game id. If a stored solo session matches
  // this game (a full page refresh mid-solo), resume it via `ReconnectSolo`
  // instead of `JoinAsHost` — the latter would force the UI back to the lobby
  // (HOST_JOINED hard-sets phase 'lobby'), stranding the solo player. Otherwise
  // do the normal host bind. The hub's `invoke()` already awaits the connection
  // handshake, so no readiness flag is needed.
  useEffect(() => {
    const soloSession = readStoredSoloSession()
    if (soloSession && soloSession.gameId === gameId) {
      setSoloMode(true)
      dispatch({
        type: 'RESTORE_PLAYER_SESSION',
        gameId: soloSession.gameId,
        token: soloSession.token,
        nickname: soloSession.nickname,
      })
      void invoke('ReconnectSolo', gameId, soloSession.token)
    } else {
      void invoke('JoinAsHost', gameId)
    }
  }, [gameId, invoke, dispatch])

  // Sub-view toggle for the 'results' phase. Re-anchored on every new
  // question so a fresh reveal always starts on the "show correct
  // answer" panel before the host advances to the leaderboard.
  const [resultsView, setResultsView] = useState<ResultsSubView>('reveal')
  const currentQuestionIndex = state.currentQuestion?.questionIndex ?? null
  useEffect(() => {
    if (state.phase === 'question') {
      setResultsView('reveal')
    }
  }, [state.phase, currentQuestionIndex])

  // Ending the session must tell the SERVER to end the game (so every player
  // receives `gameEnded` and leaves their waiting/question screen) BEFORE we
  // navigate the host away. We await the hub call first; if it fails we still
  // navigate, because the host explicitly chose to leave and the server's
  // host-abandonment sweeper will clean the game up regardless.
  const [isEnding, setIsEnding] = useState(false)
  const handleEndSession = useCallback(() => {
    if (isEnding) return
    setIsEnding(true)
    void (async () => {
      try {
        if (state.gameId) {
          await invoke('EndGame', state.gameId)
        }
      } catch {
        // Best-effort: navigate away even if the end call didn't land.
      } finally {
        window.location.href = '/dashboard'
      }
    })()
  }, [isEnding, invoke, state.gameId])

  // Solo surface: once in solo mode the presenter switchboard is replaced by
  // the player answer view (the host is now a player). Rendered before the
  // host-specific error / connecting guards because a solo restore via
  // ReconnectSolo never re-sends the PIN, so `state.pin` stays null and the
  // host connecting guard would otherwise block forever. PlayerGameView shows
  // its own connecting/error phases. The session-expiry modal is mounted here
  // too (it lives in HostShell, which solo doesn't render) so a long solo game
  // still gets the extend prompt.
  if (soloMode) {
    return (
      <>
        {state.expiryPromptSeconds !== null ? (
          <SessionExpiryModal
            seconds={state.expiryPromptSeconds}
            title={t('sessionExpiringTitle')}
            body={t('sessionExpiringBody')}
            extendLabel={t('extendSession')}
            onExtend={() => {
              if (state.gameId) {
                void invoke('Extend', state.gameId)
              }
            }}
          />
        ) : null}
        <PlayerGameView
          solo
          onAdvance={() => {
            if (state.gameId) {
              void invoke('NextQuestion', state.gameId)
            }
          }}
          onExit={handleEndSession}
          advanceLabel={
            isLastQuestion(state.currentQuestion) ? t('endGame') : t('nextQuestion')
          }
          exitLabel={t('leaveGame')}
        />
      </>
    )
  }

  if (state.phase === 'error') {
    // Hub failures arrive as stable codes; localize the ones a host can hit
    // (session expiry/abandonment included) and fall back to a generic message.
    const errorMessages: Record<string, string> = {
      SessionExpired: t('errors.SessionExpired'),
      HostAbandoned: t('errors.HostAbandoned'),
      Unauthorized: t('errors.Unauthorized'),
      GameNotFound: t('errors.GameNotFound'),
      NoQuestions: t('errors.NoQuestions'),
      InvalidState: t('errors.InvalidState'),
    }
    const message = (state.error && errorMessages[state.error]) || t('connectionError')
    return <HostErrorScreen message={message} leaveLabel={t('leaveGame')} />
  }

  // While the websocket handshake is still in flight (or the server
  // hasn't yet acknowledged `JoinAsHost`), there is no canonical PIN
  // to render. Show a connecting state instead of "—".
  if (!state.gameId || !state.pin) {
    return <HostConnectingScreen message={t('connecting')} />
  }

  return (
    <HostShell title={t('hostControlPanel')} leaveLabel={t('leaveGame')} onEnd={handleEndSession}>
      {state.expiryPromptSeconds !== null ? (
        <SessionExpiryModal
          seconds={state.expiryPromptSeconds}
          title={t('sessionExpiringTitle')}
          body={t('sessionExpiringBody')}
          extendLabel={t('extendSession')}
          onExtend={() => {
            void invoke('Extend', state.gameId!)
          }}
        />
      ) : null}
      {state.phase === 'lobby' ? (
        <LobbyView
          pin={state.pin}
          playerCount={state.playerCount}
          players={state.players}
          onStart={() => {
            void invoke('StartGame', state.gameId!)
          }}
          onPlaySolo={() => {
            setSoloMode(true)
            void invoke('StartSolo', state.gameId!)
          }}
          joinPinLabel={t('joinPinLabel')}
          joinUrlHint={t('joinUrlHint')}
          copyLinkLabel={t('copyJoinLink')}
          linkCopiedLabel={t('linkCopied')}
          playersLabel={t('playersLabel')}
          waitingLabel={t('waitingForPlayers')}
          waitingHint={t('waitingForPlayersHint')}
          startLabel={t('startGame')}
          blockedHint={t('needAtLeastOnePlayer')}
          hostLiveHeading={t('hostLiveHeading')}
          playSoloLabel={t('playSolo')}
          playSoloDescription={t('playSoloDescription')}
        />
      ) : null}

      {state.phase === 'question' && state.currentQuestion ? (
        <QuestionView
          question={state.currentQuestion}
          answersReceived={state.answersReceivedCount}
          playerCount={state.playerCount}
          onReveal={() => {
            void invoke('RevealAnswers', state.gameId!)
          }}
          progressLabel={t('questionProgress', {
            current: state.currentQuestion.questionIndex + 1,
            total: state.currentQuestion.total,
          })}
          timeLabel={t('timeLeft')}
          secondsAbbr={t('secondsAbbr')}
          answersReceivedLabel={t('answersReceived')}
          revealLabel={t('revealAnswers')}
          trueLabel={t('trueLabel')}
          falseLabel={t('falseLabel')}
          optionLabel={(n) => t('optionLabel', { n })}
        />
      ) : null}

      {state.phase === 'results' && state.currentQuestion ? (
        resultsView === 'reveal' ? (
          <RevealView
            question={state.currentQuestion}
            correctAnswer={state.correctAnswer}
            onShowLeaderboard={() => setResultsView('leaderboard')}
            progressLabel={t('questionProgress', {
              current: state.currentQuestion.questionIndex + 1,
              total: state.currentQuestion.total,
            })}
            correctAnswerLabel={t('correctAnswerLabel')}
            showLeaderboardLabel={t('showLeaderboard')}
            trueLabel={t('trueLabel')}
            falseLabel={t('falseLabel')}
            optionLabel={(n) => t('optionLabel', { n })}
          />
        ) : (
          <LeaderboardView
            entries={state.leaderboard}
            isLast={isLastQuestion(state.currentQuestion)}
            onAdvance={() => {
              void invoke('NextQuestion', state.gameId!)
            }}
            heading={t('leaderboard')}
            rankLabel={t('rankLabel')}
            nicknameLabel={t('nicknameLabel')}
            scoreLabel={t('scoreLabel')}
            emptyLabel={t('noLeaderboard')}
            advanceLabel={
              isLastQuestion(state.currentQuestion) ? t('endGame') : t('nextQuestion')
            }
          />
        )
      ) : null}

      {state.phase === 'leaderboard' ? (
        <LeaderboardView
          entries={state.leaderboard}
          isLast={isLastQuestion(state.currentQuestion)}
          onAdvance={() => {
            void invoke('NextQuestion', state.gameId!)
          }}
          heading={t('leaderboard')}
          rankLabel={t('rankLabel')}
          nicknameLabel={t('nicknameLabel')}
          scoreLabel={t('scoreLabel')}
          emptyLabel={t('noLeaderboard')}
          advanceLabel={
            isLastQuestion(state.currentQuestion) ? t('endGame') : t('nextQuestion')
          }
        />
      ) : null}

      {state.phase === 'final' ? (
        <FinalView
          entries={state.leaderboard}
          heading={t('gameEnded')}
          subheading={t('finalResults')}
          rankLabel={t('rankLabel')}
          nicknameLabel={t('nicknameLabel')}
          scoreLabel={t('scoreLabel')}
          emptyLabel={t('noLeaderboard')}
          backLabel={t('backToDashboard')}
        />
      ) : null}
    </HostShell>
  )
}

/**
 * The total field on `QuestionStartedPayload` is 1-based; questionIndex
 * is 0-based. After the host clicks "reveal" on question N (0-based),
 * `currentQuestion` still references that question — so "last question"
 * is `questionIndex + 1 >= total`.
 */
function isLastQuestion(question: QuestionStartedPayload | null): boolean {
  if (!question) {
    return false
  }
  return question.questionIndex + 1 >= question.total
}

// ─────────────────────────────────────────────────────────────────────────────
// Session-expiry prompt
// ─────────────────────────────────────────────────────────────────────────────

type SessionExpiryModalProps = {
  seconds: number
  title: string
  body: string
  extendLabel: string
  onExtend: () => void
}

/**
 * Blocking prompt shown when the server warns the session is about to expire
 * (host idle past the configured lifetime). Counts down the response window;
 * if it hits zero the server ends the game and the host drops to the error
 * screen. Clicking extend invokes `Extend`, which the server acks via
 * `sessionExtended` → the prompt clears.
 */
function SessionExpiryModal({ seconds, title, body, extendLabel, onExtend }: SessionExpiryModalProps) {
  const [remaining, setRemaining] = useState(seconds)

  useEffect(() => {
    setRemaining(seconds)
    const id = window.setInterval(() => {
      setRemaining((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => window.clearInterval(id)
  }, [seconds])

  const minutes = Math.floor(remaining / 60)
  const secs = remaining % 60
  const clock = `${minutes}:${secs.toString().padStart(2, '0')}`

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expiry-title"
    >
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-brand-900/95 ps-7 pe-7 py-8 text-center shadow-2xl">
        <div
          className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-400/15 ring-2 ring-amber-300/40"
          aria-hidden="true"
        >
          <Clock className="h-7 w-7 text-amber-200" />
        </div>
        <h2
          id="session-expiry-title"
          className="mt-5 font-display text-2xl font-bold tracking-tight text-white"
        >
          {title}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-brand-100/80">{body}</p>
        <div
          className="mt-5 font-display text-4xl font-bold tabular-nums text-amber-200"
          aria-live="polite"
          aria-atomic="true"
        >
          {clock}
        </div>
        <Button
          type="button"
          size="lg"
          onClick={onExtend}
          className="mt-6 h-12 w-full bg-white text-brand-900 text-base font-bold hover:bg-brand-50"
        >
          {extendLabel}
        </Button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Shell + ambient atmosphere
// ─────────────────────────────────────────────────────────────────────────────

type HostShellProps = {
  title: string
  leaveLabel: string
  onEnd?: (() => void) | undefined
  children: React.ReactNode
}

/**
 * Shared chrome for every phase: dark brand gradient, atmospheric blurred
 * radial accents, a centered max-width column, and the persistent
 * header (panel title + leave affordance). Each phase view fills the
 * `children` slot beneath.
 *
 * `onEnd`, when provided, makes the leave control END THE GAME on the server
 * (broadcasting `gameEnded` so every player leaves the lobby/question screen)
 * before navigating away. Without it players would sit on "waiting for the
 * host" forever, because simply navigating the host's browser away never
 * tells the hub the session is over. When `onEnd` is omitted (error/terminal
 * screens with no live game) the control is a plain link to the dashboard.
 */
function HostShell({ title, leaveLabel, onEnd, children }: HostShellProps) {
  return (
    <main className="relative min-h-screen overflow-hidden bg-gradient-to-bl from-brand-900 via-brand-800 to-brand-900 text-white">
      {/* Atmospheric layers — soft radial highlight plus two off-canvas
          blurred orbs so the dark backdrop never reads as flat. Decorative
          only, fully behind interactive content. */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.18),transparent_60%)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -top-32 -end-32 h-96 w-96 rounded-full bg-brand-400/20 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-32 -start-32 h-96 w-96 rounded-full bg-brand-300/10 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 sm:px-6 lg:px-8 py-10">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div
              className="inline-block h-1 w-12 rounded-full bg-brand-300/70 mb-3"
              aria-hidden="true"
            />
            <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-white">
              {title}
            </h1>
          </div>

          {onEnd ? (
            <Button
              type="button"
              onClick={onEnd}
              variant="outline"
              size="sm"
              className="self-start sm:self-auto gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span>{leaveLabel}</span>
            </Button>
          ) : (
            <a href="/dashboard" className="self-start sm:self-auto">
              <Button
                variant="outline"
                size="sm"
                className="gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                <span>{leaveLabel}</span>
              </Button>
            </a>
          )}
        </header>

        <div className="mt-10 flex flex-1 flex-col">{children}</div>
      </div>
    </main>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase: Lobby
// ─────────────────────────────────────────────────────────────────────────────

type LobbyViewProps = {
  pin: string
  playerCount: number
  players: { playerId: string; nickname: string }[]
  onStart: () => void
  onPlaySolo: () => void
  joinPinLabel: string
  joinUrlHint: string
  copyLinkLabel: string
  linkCopiedLabel: string
  playersLabel: string
  waitingLabel: string
  waitingHint: string
  startLabel: string
  blockedHint: string
  hostLiveHeading: string
  playSoloLabel: string
  playSoloDescription: string
}

function LobbyView({
  pin,
  playerCount,
  players,
  onStart,
  onPlaySolo,
  joinPinLabel,
  joinUrlHint,
  copyLinkLabel,
  linkCopiedLabel,
  playersLabel,
  waitingLabel,
  waitingHint,
  startLabel,
  blockedHint,
  hostLiveHeading,
  playSoloLabel,
  playSoloDescription,
}: LobbyViewProps) {
  const canStart = playerCount > 0
  return (
    <>
      <section className="flex flex-1 flex-col items-center justify-center text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-100 backdrop-blur-sm">
          <Hash className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{joinPinLabel}</span>
        </div>

        <p
          className="mt-6 font-display text-7xl sm:text-8xl lg:text-9xl font-bold tracking-[0.18em] text-white tabular-nums drop-shadow-[0_8px_30px_rgba(0,0,0,0.45)]"
          aria-label={`${joinPinLabel}: ${pin}`}
        >
          {pin}
        </p>

        <p className="mt-4 text-base sm:text-lg text-brand-100/80">
          {joinUrlHint}{' '}
          <span className="font-semibold text-white">{getJoinHostname()}</span>
        </p>

        <JoinLinkCopy
          pin={pin}
          copyLinkLabel={copyLinkLabel}
          linkCopiedLabel={linkCopiedLabel}
        />
      </section>

      {/* Two explicit paths, side by side: host a live multiplayer game (left,
          gated on ≥1 player) vs play solo right now (right, always enabled). */}
      <section className="mt-12 grid gap-6 sm:grid-cols-2">
        <div className="flex flex-col gap-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-brand-100/80">
            {hostLiveHeading}
          </h2>
          <PlayerCountCard
            count={playerCount}
            players={players}
            label={playersLabel}
            waitingLabel={waitingLabel}
            waitingHint={waitingHint}
          />
          <StartGamePanel
            canStart={canStart}
            onStart={onStart}
            startLabel={startLabel}
            blockedHint={blockedHint}
          />
        </div>

        <SoloPanel
          heading={playSoloLabel}
          description={playSoloDescription}
          onPlaySolo={onPlaySolo}
        />
      </section>
    </>
  )
}

type SoloPanelProps = {
  heading: string
  description: string
  onPlaySolo: () => void
}

/**
 * The "play solo" path: the host runs through the quiz themselves on this one
 * device. The button is ALWAYS enabled — it is the no-players path, so the
 * `canStart` gate that guards the live game must never affect it.
 */
function SoloPanel({ heading, description, onPlaySolo }: SoloPanelProps) {
  return (
    <div className="flex flex-col">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-brand-100/80">
        {' '}
      </h2>
      <div className="mt-4 flex flex-1 flex-col justify-between rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm ps-6 pe-6 py-6 shadow-2xl">
        <div>
          <div className="flex items-center gap-3">
            <span
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-brand-100"
              aria-hidden="true"
            >
              <User className="h-5 w-5" />
            </span>
            <span className="font-display text-xl font-bold text-white">{heading}</span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-brand-100/80">{description}</p>
        </div>
        <Button
          type="button"
          onClick={onPlaySolo}
          size="lg"
          className="mt-6 h-14 w-full gap-2 bg-white text-brand-900 text-lg font-bold shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.01] active:scale-100"
        >
          <Sparkles className="h-6 w-6" aria-hidden="true" />
          <span>{heading}</span>
        </Button>
      </div>
    </div>
  )
}

type JoinLinkCopyProps = {
  pin: string
  copyLinkLabel: string
  linkCopiedLabel: string
}

/**
 * Copyable join link for the lobby. Renders the absolute `/play?pin=…`
 * URL in an LTR code pill (it's a Latin URL on an Arabic page) with a
 * copy button beside it. On click we prefer the async Clipboard API and
 * fall back to a hidden-input `execCommand('copy')` for non-secure
 * contexts; if both fail the URL stays visible so the host can copy it
 * by hand. The "copied" confirmation swaps the icon/label for ~2s.
 */
function JoinLinkCopy({ pin, copyLinkLabel, linkCopiedLabel }: JoinLinkCopyProps) {
  const url = getJoinUrl(pin)
  const [copied, setCopied] = useState(false)

  // Clear the transient confirmation after two seconds. Keyed on `copied`
  // so a fresh click while the badge is up resets the window; cleaned up
  // on unmount so a late timer never fires into a torn-down component.
  useEffect(() => {
    if (!copied) return
    const id = window.setTimeout(() => setCopied(false), 2000)
    return () => window.clearTimeout(id)
  }, [copied])

  const handleCopy = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
      } else {
        legacyCopyToClipboard(url)
      }
      setCopied(true)
    } catch {
      // Clipboard unavailable or blocked (denied permission / insecure
      // origin). Leave the URL on screen so the host can select it
      // manually rather than crashing the lobby.
    }
  }

  return (
    <div className="mt-6 flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
      <code
        dir="ltr"
        className="max-w-full truncate rounded-full border border-white/15 bg-white/5 ps-4 pe-4 py-2 font-mono text-sm text-brand-100/90 backdrop-blur-sm"
      >
        {url}
      </code>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copyLinkLabel}
        className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/20 bg-white/5 ps-4 pe-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10"
      >
        {copied ? (
          <>
            <Check className="h-4 w-4" aria-hidden="true" />
            <span>{linkCopiedLabel}</span>
          </>
        ) : (
          <>
            <Copy className="h-4 w-4" aria-hidden="true" />
            <span>{copyLinkLabel}</span>
          </>
        )}
      </button>
    </div>
  )
}

type PlayerCountCardProps = {
  count: number
  players: { playerId: string; nickname: string }[]
  label: string
  waitingLabel: string
  waitingHint: string
}

/**
 * Aggregate count card. Shows the connected-player count headline plus a
 * wrapping grid of name pills derived from the authoritative server roster.
 * The numeral uses tabular-nums so the digit width stays stable as players
 * join, preventing the card from jittering when the count rolls over.
 */
function PlayerCountCard({
  count,
  players,
  label,
  waitingLabel,
  waitingHint,
}: PlayerCountCardProps) {
  const hasPlayers = count > 0
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm ps-6 pe-6 py-6 shadow-2xl">
      <div className="flex items-center gap-3">
        <span
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-brand-100"
          aria-hidden="true"
        >
          <Users className="h-5 w-5" />
        </span>
        <span className="text-sm font-semibold uppercase tracking-wider text-brand-100/80">
          {label}
        </span>
      </div>

      <div className="mt-4 flex items-baseline gap-3">
        <span
          className="font-display text-6xl font-bold tabular-nums text-white"
          aria-live="polite"
          aria-atomic="true"
        >
          {count}
        </span>
        {!hasPlayers ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-xs text-brand-100/80">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            <span>{waitingLabel}</span>
          </span>
        ) : null}
      </div>

      {hasPlayers ? (
        <ul
          role="list"
          className="mt-4 flex flex-wrap gap-2"
          aria-label={label}
        >
          {players.map((p) => (
            <li key={p.playerId}>
              <span className="inline-flex items-center rounded-full bg-white/15 ps-3 pe-3 py-1 text-sm font-semibold text-white">
                {p.nickname}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm leading-relaxed text-brand-100/70">
          {waitingHint}
        </p>
      )}
    </div>
  )
}

type StartGamePanelProps = {
  canStart: boolean
  onStart: () => void
  startLabel: string
  blockedHint: string
}

function StartGamePanel({
  canStart,
  onStart,
  startLabel,
  blockedHint,
}: StartGamePanelProps) {
  return (
    <div className="flex flex-col justify-between rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm ps-6 pe-6 py-6 shadow-2xl">
      <p className="text-sm leading-relaxed text-brand-100/80">
        {canStart ? '\u00A0' : blockedHint}
      </p>
      <Button
        type="button"
        onClick={onStart}
        disabled={!canStart}
        size="lg"
        className="mt-4 h-14 w-full gap-2 bg-white text-brand-900 text-lg font-bold shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.01] active:scale-100 disabled:bg-white/20 disabled:text-white/50 disabled:shadow-none"
      >
        <Play className="h-6 w-6 fill-current" aria-hidden="true" />
        <span>{startLabel}</span>
      </Button>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase: Question (live)
// ─────────────────────────────────────────────────────────────────────────────

type QuestionViewProps = {
  question: QuestionStartedPayload
  answersReceived: number
  playerCount: number
  onReveal: () => void
  progressLabel: string
  timeLabel: string
  secondsAbbr: string
  answersReceivedLabel: string
  revealLabel: string
  trueLabel: string
  falseLabel: string
  optionLabel: (n: number) => string
}

function QuestionView({
  question,
  answersReceived,
  playerCount,
  onReveal,
  progressLabel,
  timeLabel,
  secondsAbbr,
  answersReceivedLabel,
  revealLabel,
  trueLabel,
  falseLabel,
  optionLabel,
}: QuestionViewProps) {
  const timeLeft = useCountdown(question.questionIndex, question.timeSeconds)
  const questionHtml = useMemo(() => renderMathPreview(question.text), [question.text])

  return (
    <>
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
        <MetaPill
          label={progressLabel}
          value=""
          icon={<MessageSquare className="h-4 w-4" aria-hidden="true" />}
        />
        <TimerPill
          remaining={timeLeft}
          total={question.timeSeconds}
          label={timeLabel}
          unit={secondsAbbr}
        />
        <MetaPill
          label={answersReceivedLabel}
          value={`${answersReceived} / ${playerCount}`}
          icon={<Users className="h-4 w-4" aria-hidden="true" />}
        />
      </section>

      <section className="mt-8 flex flex-1 flex-col">
        <article className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl ps-6 pe-6 sm:ps-10 sm:pe-10 py-10">
          <div
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-relaxed text-white text-center"
            // KaTeX returns balanced, sanitised HTML (trust:false, strict:true);
            // plain text outside `$…$` delimiters is HTML-escaped before
            // joining, so injecting via dangerouslySetInnerHTML is safe.
            dangerouslySetInnerHTML={{ __html: questionHtml }}
          />
        </article>

        <section className="mt-8">
          {question.type === 'MultipleChoice' ? (
            <OptionGrid options={question.options ?? []} optionLabel={optionLabel} />
          ) : question.type === 'OrderSequence' ? (
            <OrderItemsGrid items={question.items ?? []} itemOrder={question.itemOrder ?? []} />
          ) : (
            <TrueFalseGrid trueLabel={trueLabel} falseLabel={falseLabel} />
          )}
        </section>

        <section className="mt-10 flex justify-center">
          <Button
            type="button"
            onClick={onReveal}
            size="lg"
            className="h-14 gap-2 bg-white text-brand-900 text-lg font-bold shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.01] active:scale-100 ps-8 pe-8"
          >
            <Eye className="h-5 w-5" aria-hidden="true" />
            <span>{revealLabel}</span>
          </Button>
        </section>
      </section>
    </>
  )
}

type MetaPillProps = {
  label: string
  value: string
  icon: React.ReactNode
}

function MetaPill({ label, value, icon }: MetaPillProps) {
  return (
    <div className="flex items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm ps-4 pe-4 py-3 text-sm">
      <span
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-brand-100"
        aria-hidden="true"
      >
        {icon}
      </span>
      <div className="flex flex-col items-start text-start">
        <span className="text-xs uppercase tracking-wider text-brand-100/70">
          {label}
        </span>
        {value ? (
          <span
            className="font-display text-xl font-bold tabular-nums text-white"
            aria-live="polite"
            aria-atomic="true"
          >
            {value}
          </span>
        ) : null}
      </div>
    </div>
  )
}

type TimerPillProps = {
  remaining: number
  total: number
  label: string
  unit: string
}

/**
 * Time-remaining pill. Renders as a single horizontal element with the
 * label, the numeral (tabular-nums to keep digits aligned as it
 * decrements) and a thin progress bar at the bottom. Colour shifts
 * amber → red as the timer approaches zero so the host gets a peripheral
 * cue without having to read the number.
 */
function TimerPill({ remaining, total, label, unit }: TimerPillProps) {
  const pct = total > 0 ? Math.max(0, Math.min(100, (remaining / total) * 100)) : 0
  const isUrgent = remaining <= 5
  const isCaution = remaining <= 10 && !isUrgent
  const numeralColor = isUrgent
    ? 'text-red-300'
    : isCaution
      ? 'text-amber-300'
      : 'text-white'
  const barColor = isUrgent
    ? 'bg-red-400'
    : isCaution
      ? 'bg-amber-400'
      : 'bg-brand-300'
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm ps-4 pe-4 py-3">
      <div className="flex items-center justify-center gap-3">
        <span
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-brand-100"
          aria-hidden="true"
        >
          <Clock className="h-4 w-4" />
        </span>
        <div className="flex flex-col items-start text-start">
          <span className="text-xs uppercase tracking-wider text-brand-100/70">
            {label}
          </span>
          <span
            className={`font-display text-xl font-bold tabular-nums ${numeralColor}`}
            aria-live="polite"
            aria-atomic="true"
          >
            {remaining}
            <span className="ms-1 text-sm text-brand-100/70">{unit}</span>
          </span>
        </div>
      </div>
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-white/5"
        aria-hidden="true"
      >
        <div
          className={`h-full transition-[width] duration-1000 ease-linear ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

type OptionGridProps = {
  options: string[]
  optionLabel: (n: number) => string
  correctAnswer?: string | null
}

/**
 * MCQ option grid. Kahoot-style colour assignments (red / blue / amber /
 * emerald) make options instantly distinguishable from across a room,
 * which matters because players see the same colours in their own
 * client UI. The grid is non-interactive on the host screen — these
 * are display cards, not buttons. When `correctAnswer` is provided
 * (results phase) the matching card scales up, gains a ring, and
 * shows a check icon; non-matching cards dim.
 */
function OptionGrid({ options, optionLabel, correctAnswer }: OptionGridProps) {
  const showReveal = typeof correctAnswer === 'string' && correctAnswer.length > 0
  return (
    <ul role="list" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {options.map((opt, i) => {
        const isCorrect = showReveal && isOptionCorrect(i, opt, correctAnswer)
        const dim = showReveal && !isCorrect
        return (
          <li key={`${i}-${opt}`}>
            <div
              className={`relative flex min-h-24 items-center gap-4 rounded-2xl ps-5 pe-5 py-5 text-start shadow-xl transition-all duration-300 ${OPTION_COLORS[i % OPTION_COLORS.length]} ${
                dim ? 'opacity-30 saturate-50' : ''
              } ${
                isCorrect
                  ? 'scale-[1.03] ring-4 ring-white/80 ring-offset-2 ring-offset-brand-900'
                  : ''
              }`}
            >
              <span
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black/20 text-white font-bold font-display text-sm"
                aria-label={optionLabel(i + 1)}
              >
                {i + 1}
              </span>
              <span className="text-xl sm:text-2xl font-bold font-display text-white leading-snug">
                {opt}
              </span>
              {isCorrect ? (
                <CheckCircle2
                  className="absolute end-4 top-4 h-7 w-7 text-white drop-shadow-md"
                  aria-hidden="true"
                />
              ) : null}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

const OPTION_COLORS = [
  'bg-red-500/90',
  'bg-blue-500/90',
  'bg-amber-500/90',
  'bg-emerald-500/90',
] as const

type TrueFalseGridProps = {
  trueLabel: string
  falseLabel: string
  correctAnswer?: string | null
}

function TrueFalseGrid({ trueLabel, falseLabel, correctAnswer }: TrueFalseGridProps) {
  const showReveal = typeof correctAnswer === 'string' && correctAnswer.length > 0
  const correctValue = showReveal ? normalizeBool(correctAnswer) : null
  const cards: Array<{ value: 'true' | 'false'; label: string; tone: string }> = [
    { value: 'true', label: trueLabel, tone: 'bg-emerald-500/90' },
    { value: 'false', label: falseLabel, tone: 'bg-red-500/90' },
  ]
  return (
    <ul role="list" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {cards.map((card) => {
        const isCorrect = showReveal && correctValue === card.value
        const dim = showReveal && !isCorrect
        return (
          <li key={card.value}>
            <div
              className={`relative flex min-h-28 items-center justify-center rounded-2xl ps-5 pe-5 py-5 shadow-xl transition-all duration-300 ${card.tone} ${
                dim ? 'opacity-30 saturate-50' : ''
              } ${
                isCorrect
                  ? 'scale-[1.03] ring-4 ring-white/80 ring-offset-2 ring-offset-brand-900'
                  : ''
              }`}
            >
              <span className="font-display text-3xl sm:text-4xl font-bold text-white">
                {card.label}
              </span>
              {isCorrect ? (
                <CheckCircle2
                  className="absolute end-4 top-4 h-7 w-7 text-white drop-shadow-md"
                  aria-hidden="true"
                />
              ) : null}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

type OrderItemsGridProps = {
  items: string[]
  /**
   * Maps each displayed-item slot back to its canonical (correct) index.
   * For example, `itemOrder[2] = 0` means the third tile on the projector
   * is actually the first item in the correct order.
   */
  itemOrder: number[]
  /**
   * When true, render with the canonical position visible next to each
   * tile (1, 2, 3, …) so the audience can read the correct sequence.
   */
  revealCorrect?: boolean
}

/**
 * Order/Sequence projector grid. During the question phase, items are
 * shown in the server-shuffled display order with no answer cues so the
 * room can read them off the screen. On reveal, each tile is annotated
 * with its canonical position (1 = first, 2 = second, …) so the audience
 * sees the correct sequence at a glance.
 */
function OrderItemsGrid({ items, itemOrder, revealCorrect = false }: OrderItemsGridProps) {
  return (
    <ul role="list" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {items.map((item, i) => {
        const canonical = itemOrder[i]
        const correctPosition =
          revealCorrect && typeof canonical === 'number' ? canonical + 1 : null
        return (
          <li key={`${i}-${item}`}>
            <div
              className={`relative flex min-h-20 items-center gap-4 rounded-2xl ps-5 pe-5 py-5 text-start shadow-xl transition-all duration-300 ${
                revealCorrect ? 'bg-amber-500/90' : 'bg-white/10 backdrop-blur-sm'
              }`}
            >
              <span
                className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-bold font-display text-base tabular-nums ${
                  revealCorrect ? 'bg-white text-amber-700' : 'bg-black/25 text-white'
                }`}
                aria-hidden="true"
              >
                {correctPosition ?? i + 1}
              </span>
              <span className="text-xl sm:text-2xl font-bold font-display text-white leading-snug">
                {item}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Coerce a wire-format truth value into our internal `'true' | 'false'`
 * literal. The .NET hub serialises `bool` as `"True"` / `"False"` but
 * we accept either casing and the lower-case JS form too, so the UI
 * stays correct if the contract ever drifts.
 */
function normalizeBool(value: string): 'true' | 'false' | null {
  const v = value.trim().toLowerCase()
  if (v === 'true') return 'true'
  if (v === 'false') return 'false'
  return null
}

/**
 * Decide whether an MCQ option is the correct one. The server may emit
 * the correct answer as either the option text or the option index
 * (stringified). We probe both — index match wins because it's
 * unambiguous, text match is the fallback.
 */
function isOptionCorrect(
  index: number,
  optionText: string,
  correctAnswer: string,
): boolean {
  const trimmed = correctAnswer.trim()
  if (trimmed === String(index)) {
    return true
  }
  return trimmed === optionText.trim()
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase: Results (reveal sub-view)
// ─────────────────────────────────────────────────────────────────────────────

type RevealViewProps = {
  question: QuestionStartedPayload
  correctAnswer: string | null
  onShowLeaderboard: () => void
  progressLabel: string
  correctAnswerLabel: string
  showLeaderboardLabel: string
  trueLabel: string
  falseLabel: string
  optionLabel: (n: number) => string
}

function RevealView({
  question,
  correctAnswer,
  onShowLeaderboard,
  progressLabel,
  correctAnswerLabel,
  showLeaderboardLabel,
  trueLabel,
  falseLabel,
  optionLabel,
}: RevealViewProps) {
  const questionHtml = useMemo(() => renderMathPreview(question.text), [question.text])
  return (
    <>
      <section className="flex justify-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-emerald-300/30 bg-emerald-400/10 px-4 py-1.5 text-sm font-semibold text-emerald-100">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          <span>{correctAnswerLabel}</span>
          <span className="text-brand-100/70">·</span>
          <span className="text-brand-100/80">{progressLabel}</span>
        </span>
      </section>

      <section className="mt-6 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl ps-6 pe-6 sm:ps-10 sm:pe-10 py-8">
        <div
          className="font-display text-2xl sm:text-3xl font-bold leading-relaxed text-white text-center"
          dangerouslySetInnerHTML={{ __html: questionHtml }}
        />
      </section>

      <section className="mt-8">
        {question.type === 'MultipleChoice' ? (
          <OptionGrid
            options={question.options ?? []}
            optionLabel={optionLabel}
            correctAnswer={correctAnswer}
          />
        ) : question.type === 'OrderSequence' ? (
          <OrderItemsGrid
            items={question.items ?? []}
            itemOrder={question.itemOrder ?? []}
            revealCorrect
          />
        ) : (
          <TrueFalseGrid
            trueLabel={trueLabel}
            falseLabel={falseLabel}
            correctAnswer={correctAnswer}
          />
        )}
      </section>

      <section className="mt-10 flex justify-center">
        <Button
          type="button"
          onClick={onShowLeaderboard}
          size="lg"
          className="h-14 gap-2 bg-white text-brand-900 text-lg font-bold shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.01] active:scale-100 ps-8 pe-8"
        >
          <Trophy className="h-5 w-5" aria-hidden="true" />
          <span>{showLeaderboardLabel}</span>
        </Button>
      </section>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase: Leaderboard (interstitial between questions)
// ─────────────────────────────────────────────────────────────────────────────

type LeaderboardViewProps = {
  entries: LeaderboardEntry[]
  isLast: boolean
  onAdvance: () => void
  heading: string
  rankLabel: string
  nicknameLabel: string
  scoreLabel: string
  emptyLabel: string
  advanceLabel: string
}

function LeaderboardView({
  entries,
  isLast,
  onAdvance,
  heading,
  rankLabel,
  nicknameLabel,
  scoreLabel,
  emptyLabel,
  advanceLabel,
}: LeaderboardViewProps) {
  return (
    <>
      <section className="flex justify-center">
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
          {heading}
        </h2>
      </section>

      <section className="mt-8 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl ps-2 pe-2 sm:ps-6 sm:pe-6 py-4">
        <LeaderboardList
          entries={entries}
          rankLabel={rankLabel}
          nicknameLabel={nicknameLabel}
          scoreLabel={scoreLabel}
          emptyLabel={emptyLabel}
        />
      </section>

      <section className="mt-10 flex justify-center">
        <Button
          type="button"
          onClick={onAdvance}
          size="lg"
          className="h-14 gap-2 bg-white text-brand-900 text-lg font-bold shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.01] active:scale-100 ps-8 pe-8"
        >
          {isLast ? (
            <Flag className="h-5 w-5" aria-hidden="true" />
          ) : (
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          )}
          <span>{advanceLabel}</span>
        </Button>
      </section>
    </>
  )
}

type LeaderboardListProps = {
  entries: LeaderboardEntry[]
  rankLabel: string
  nicknameLabel: string
  scoreLabel: string
  emptyLabel: string
}

/**
 * Tabular top-N list. Truncated to ten rows because the projector
 * format leaves no room for more, and the long tail rarely matters to
 * an audience watching from the back of a room. Crown highlight on
 * rank 1, lighter accent on 2 / 3 — a small dose of editorial drama
 * without descending into ribbon-banner kitsch.
 */
function LeaderboardList({
  entries,
  rankLabel,
  nicknameLabel,
  scoreLabel,
  emptyLabel,
}: LeaderboardListProps) {
  if (entries.length === 0) {
    return (
      <p className="px-6 py-10 text-center text-base text-brand-100/70">
        {emptyLabel}
      </p>
    )
  }
  const top = entries.slice(0, 10)
  return (
    <table className="w-full table-fixed text-base">
      <thead>
        <tr className="text-xs uppercase tracking-wider text-brand-100/60">
          <th scope="col" className="w-20 ps-4 pe-2 py-3 text-start font-semibold">
            {rankLabel}
          </th>
          <th scope="col" className="ps-2 pe-2 py-3 text-start font-semibold">
            {nicknameLabel}
          </th>
          <th scope="col" className="w-28 ps-2 pe-4 py-3 text-end font-semibold">
            {scoreLabel}
          </th>
        </tr>
      </thead>
      <tbody>
        {top.map((entry, i) => {
          const rank = entry.rank ?? i + 1
          const isTop = rank === 1
          const isPodium = rank === 2 || rank === 3
          return (
            <tr
              key={`${rank}-${entry.nickname}`}
              className={`border-t border-white/5 ${
                isTop
                  ? 'bg-amber-300/10'
                  : isPodium
                    ? 'bg-white/5'
                    : ''
              }`}
            >
              <td className="ps-4 pe-2 py-4">
                <span
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-full font-bold font-display tabular-nums text-sm ${
                    isTop
                      ? 'bg-amber-300 text-amber-900'
                      : isPodium
                        ? 'bg-white/15 text-white'
                        : 'bg-white/10 text-brand-100'
                  }`}
                >
                  {rank}
                </span>
              </td>
              <td className="ps-2 pe-2 py-4">
                <span
                  className={`font-display font-bold truncate ${
                    isTop ? 'text-2xl text-white' : 'text-xl text-white/90'
                  }`}
                >
                  {entry.nickname}
                </span>
              </td>
              <td className="ps-2 pe-4 py-4 text-end">
                <span
                  className={`font-display font-bold tabular-nums ${
                    isTop ? 'text-2xl text-amber-200' : 'text-xl text-white'
                  }`}
                >
                  {entry.score}
                </span>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase: Final
// ─────────────────────────────────────────────────────────────────────────────

type FinalViewProps = {
  entries: LeaderboardEntry[]
  heading: string
  subheading: string
  rankLabel: string
  nicknameLabel: string
  scoreLabel: string
  emptyLabel: string
  backLabel: string
}

function FinalView({
  entries,
  heading,
  subheading,
  rankLabel,
  nicknameLabel,
  scoreLabel,
  emptyLabel,
  backLabel,
}: FinalViewProps) {
  return (
    <>
      <section className="flex flex-col items-center text-center">
        <span
          className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-amber-300/15 ring-1 ring-amber-300/40"
          aria-hidden="true"
        >
          <Trophy className="h-8 w-8 text-amber-200" />
        </span>
        <h2 className="mt-5 font-display text-5xl sm:text-6xl font-bold tracking-tight text-white">
          {heading}
        </h2>
        <p className="mt-3 text-base sm:text-lg text-brand-100/80">{subheading}</p>
      </section>

      <section className="mt-10 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl ps-2 pe-2 sm:ps-6 sm:pe-6 py-4">
        <LeaderboardList
          entries={entries}
          rankLabel={rankLabel}
          nicknameLabel={nicknameLabel}
          scoreLabel={scoreLabel}
          emptyLabel={emptyLabel}
        />
      </section>

      <section className="mt-10 flex justify-center">
        <a href="/dashboard" className="inline-block">
          <Button
            type="button"
            size="lg"
            className="h-14 gap-2 bg-white text-brand-900 text-lg font-bold shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.01] active:scale-100 ps-8 pe-8"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            <span>{backLabel}</span>
          </Button>
        </a>
      </section>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Connecting / Error screens
// ─────────────────────────────────────────────────────────────────────────────

function HostConnectingScreen({ message }: { message: string }) {
  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-900 via-brand-800 to-brand-900 text-white">
      <div className="text-center">
        <div
          className="mx-auto h-14 w-14 animate-spin rounded-full border-4 border-white/20 border-t-white"
          role="status"
          aria-live="polite"
        >
          <span className="sr-only">{message}</span>
        </div>
        <p className="mt-6 text-lg text-brand-100/90">{message}</p>
      </div>
    </main>
  )
}

type HostErrorScreenProps = {
  message: string
  leaveLabel: string
}

/**
 * Terminal error surface: shown when the reducer's `phase` flips to
 * `error` (typically from `connection.start()` rejecting or the server
 * pushing a `gameLost` reason). Re-uses the `leaveGame` label as the
 * exit affordance — semantically this is the host abandoning a broken
 * session, which lines up with what that string means elsewhere.
 */
function HostErrorScreen({ message, leaveLabel }: HostErrorScreenProps) {
  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-900 via-brand-800 to-brand-900 text-white px-4">
      <div
        className="max-w-md rounded-2xl bg-red-500/10 border border-red-400/30 text-red-100 ps-6 pe-6 py-6 text-center"
        role="alert"
      >
        <p className="text-base leading-relaxed">{message}</p>
        <a href="/dashboard" className="mt-6 inline-block">
          <Button
            variant="outline"
            className="gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            <span>{leaveLabel}</span>
          </Button>
        </a>
      </div>
    </main>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Hooks + helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 1-Hz countdown that resets every time the question index changes. We
 * key on `questionIndex` rather than `timeSeconds` so a question whose
 * server timer happens to match the previous question's seconds still
 * re-anchors visibly.
 *
 * The interval is intentionally not synchronised with the server clock
 * — the server is authoritative for transitions, this is a UI affordance
 * that helps the host pace the room. Drift of ≤1s is acceptable for a
 * 5–120s window.
 */
function useCountdown(questionIndex: number, totalSeconds: number): number {
  const [remaining, setRemaining] = useState(totalSeconds)
  useEffect(() => {
    setRemaining(totalSeconds)
    if (totalSeconds <= 0) {
      return
    }
    const interval = window.setInterval(() => {
      setRemaining((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => {
      window.clearInterval(interval)
    }
  }, [questionIndex, totalSeconds])
  return remaining
}

/**
 * Render a quiz question prompt with inline / display KaTeX math
 * substitutions. Plain text outside the delimiters is HTML-escaped so a
 * pasted `<script>` lands as literal text in the preview pane, not as
 * executable markup.
 *
 * KaTeX is invoked with `trust: false` and `strict: true` so commands
 * like `\href` (which would let an author smuggle URLs into the preview)
 * and undefined symbols are refused — KaTeX throws and we surface a
 * localised "[خطأ في الصيغة]" badge instead of failing the whole render.
 *
 * (Duplicates the private helper in `QuizEditor.tsx`. Extracting to a
 * shared module is out of scope for T41; the two copies should be kept
 * in sync until then.)
 */
function renderMathPreview(text: string): string {
  const segments: string[] = []
  const pattern = /\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$/g
  let lastIndex = 0
  let match: RegExpExecArray | null = pattern.exec(text)
  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push(escapeHtml(text.slice(lastIndex, match.index)))
    }
    const displayMath = match[1]
    const inlineMath = match[2]
    if (typeof displayMath === 'string') {
      segments.push(renderMathSafe(displayMath, true))
    } else if (typeof inlineMath === 'string') {
      segments.push(renderMathSafe(inlineMath, false))
    }
    lastIndex = pattern.lastIndex
    match = pattern.exec(text)
  }
  if (lastIndex < text.length) {
    segments.push(escapeHtml(text.slice(lastIndex)))
  }
  return segments.join('').replace(/\n/g, '<br>')
}

const KATEX_MACROS = {
  '\\RR': '\\mathbb{R}',
  '\\NN': '\\mathbb{N}',
  '\\ZZ': '\\mathbb{Z}',
  '\\QQ': '\\mathbb{Q}',
  '\\CC': '\\mathbb{C}',
} as const

function renderMathSafe(math: string, displayMode: boolean): string {
  try {
    return katex.renderToString(math, {
      displayMode,
      macros: KATEX_MACROS,
      trust: false,
      strict: true,
      throwOnError: true,
    })
  } catch {
    return '<span class="text-red-300 text-base">[خطأ في الصيغة]</span>'
  }
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Resolve the public hostname players should type to join, with a
 * graceful SSR / prerender fallback. We intentionally drop the protocol
 * and the port: hosts read this aloud to a room, so "quiz.example.com"
 * is friendlier than "https://quiz.example.com:5173".
 */
function getJoinHostname(): string {
  if (typeof window === 'undefined') {
    return ''
  }
  return window.location.host
}

/**
 * Build the absolute join URL players can open to land on `/play` with
 * the PIN prefilled (the join route reads a `pin` search param). Falls
 * back to a relative URL during SSR / prerender where `window` is absent.
 */
function getJoinUrl(pin: string): string {
  if (typeof window === 'undefined') {
    return `/play?pin=${pin}`
  }
  return `${window.location.origin}/play?pin=${pin}`
}

/**
 * Legacy clipboard path for non-secure contexts where the async
 * `navigator.clipboard` API is unavailable. Stages the text in an
 * off-screen, read-only input, selects it, and triggers the deprecated
 * `execCommand('copy')`. The input is always removed afterwards.
 */
function legacyCopyToClipboard(text: string): void {
  if (typeof document === 'undefined') {
    return
  }
  const input = document.createElement('input')
  input.value = text
  input.setAttribute('readonly', '')
  input.style.position = 'absolute'
  input.style.left = '-9999px'
  document.body.appendChild(input)
  input.select()
  try {
    document.execCommand('copy')
  } finally {
    document.body.removeChild(input)
  }
}
